import React, { useState, useEffect } from 'react';
import { Search, Video, Plus, X, Loader2, Edit3, Trash2, ExternalLink, Radio } from 'lucide-react';

export default function LiveClassesManagement() {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    meeting_url: '',
    description: ''
  });

  useEffect(() => {
    fetchLiveClasses();
  }, [searchTerm]);

  const fetchLiveClasses = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/live-classes?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setDataList(result.data || []);
      else setDataList([]);
    } catch (err) {
      console.error("Failed to load live classes:", err);
      setDataList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setFormData({
      title: '',
      meeting_url: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      meeting_url: item.meeting_url || '',
      description: item.description || ''
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const endpoint = editingId ? `update-entry/${editingId}` : 'add-entry';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/live-classes/${endpoint}`, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchLiveClasses();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error("Failed to submit form:", err);
      alert("Network communication error.");
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this live class session?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/live-classes/delete-entry/${id}`, {
        method: 'PUT'
      });
      const result = await res.json();
      if (result.success) fetchLiveClasses();
      else alert(result.message || "Failed to remove entry.");
    } catch (err) {
      console.error("Failed to delete entry:", err);
    }
  };

  // Helper to visually identify meeting platform
  const getMeetingPlatformBadge = (url) => {
    const lowerUrl = (url || '').toLowerCase();
    if (lowerUrl.includes('meet.google.com')) return { label: 'Google Meet', color: '#1a73e8', bg: '#e8f0fe' };
    if (lowerUrl.includes('zoom.us')) return { label: 'Zoom Video', color: '#2d8cff', bg: '#edf5ff' };
    if (lowerUrl.includes('teams.microsoft.com')) return { label: 'MS Teams', color: '#5b5fc7', bg: '#f0f0ff' };
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return { label: 'YouTube Live', color: '#ff0000', bg: '#ffebe8' };
    return { label: 'Direct Link', color: '#0f766e', bg: '#f0fdfa' };
  };

  // Pagination Window Range Computation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Interactive Live Classes</h2>
          <p style={styles.mainSubtitle}>Schedule and manage live interactive workshops, doubt clearing sessions, and webinars</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Schedule New Live Class
        </button>
      </div>

      {/* SEARCH AND RECORD COUNT STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by session title, URL, or topic details..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Live Classes: <strong style={{ color: '#1c6ec9' }}>{dataList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={{ ...styles.tableTh, width: '60px' }}>ID</th>
                <th style={styles.tableTh}>Class Title & Topic</th>
                <th style={styles.tableTh}>Description / Agenda</th>
                <th style={styles.tableTh}>Meeting Platform & URL</th>
                <th style={{ ...styles.tableTh, width: '130px' }}>Created Date</th>
                <th style={{ ...styles.tableTh, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr><td colSpan="6" style={styles.noDataTd}>No live classes scheduled yet.</td></tr>
              ) : (
                currentItems.map((item) => {
                  const platform = getMeetingPlatformBadge(item.meeting_url);
                  return (
                    <tr key={item.id} style={styles.tableTr}>
                      <td style={{ ...styles.tableTd, fontWeight: '700', color: '#0f172a' }}>#{item.id}</td>
                      <td style={styles.tableTd}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={styles.livePulseBox}>
                            <Radio size={14} color="#e30504" />
                          </div>
                          <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>{item.title}</strong>
                        </div>
                      </td>
                      <td style={styles.tableTd}>
                        <div style={styles.descriptionBox} title={item.description || 'No description provided.'}>
                          {item.description || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No description</span>}
                        </div>
                      </td>
                      <td style={styles.tableTd}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                          <span style={{ ...styles.platformPill, background: platform.bg, color: platform.color }}>
                            {platform.label}
                          </span>
                          <a href={item.meeting_url} target="_blank" rel="noreferrer" style={styles.linkAnchor}>
                            <ExternalLink size={12} style={{ marginRight: '4px' }} /> Join Session
                          </a>
                        </div>
                      </td>
                      <td style={styles.tableTd}>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </span>
                      </td>
                      <td style={styles.tableTd}>
                        <div style={styles.actionBtnGroup}>
                          <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Session"><Edit3 size={12} /></button>
                          <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Session"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {dataList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* POPUP MANAGEMENT MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Video size={16} color="#1c6ec9" />
                {editingId ? 'Modify Live Class Details' : 'Schedule New Live Class'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* 1. Class Title */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Class / Session Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Financial Management - Live Doubt Clearing Session"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* 2. Meeting URL */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Meeting Link / Webinar URL *</label>
                <input
                  type="url"
                  required
                  placeholder="e.g. https://meet.google.com/... or Zoom / Teams Link"
                  value={formData.meeting_url}
                  onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* 3. Description / Agenda */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Session Description / Agenda (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="Provide discussion topics, faculty details, or reference agenda..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.formTextArea}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>
                  {editingId ? 'Save Changes' : 'Publish Live Class'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '380px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '14px 14px', color: '#475569', fontSize: '13.5px', verticalAlign: 'middle' },
  noDataTd: { padding: '36px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  livePulseBox: { width: '28px', height: '28px', borderRadius: '6px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  descriptionBox: { maxWidth: '340px', whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.4', fontSize: '12.5px', color: '#475569' },
  platformPill: { display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.3px' },
  linkAnchor: { display: 'inline-flex', alignItems: 'center', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1c6ec9', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', textDecoration: 'none' },
  actionBtnGroup: { display: 'flex', gap: '6px' },
  editRowBtn: { background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  deleteRowBtn: { background: '#ffffff', border: '1px solid #fecaca', color: '#b91c1c', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '540px', maxHeight: '85vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px', overflowY: 'auto' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formTextArea: { width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }
};